"use client"

import { useState, useRef, useEffect, useMemo } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  Play,
  Pause,
  Search,
  Check,
  Volume2,
  Loader2,
} from "lucide-react"
import { VoiceGenderIcon } from "@/components/agents/voice/voice-gender-icon"
import { cn } from "@/lib/utils"
import type { Voice } from "@/hooks/voice"

interface VoiceSelectorProps {
  voices: Voice[]
  selectedVoiceId: string
  onSelect: (voiceId: string) => void
  isLoading?: boolean
  error?: string | null
}

export function VoiceSelector({
  voices,
  selectedVoiceId,
  onSelect,
  isLoading,
  error,
}: VoiceSelectorProps) {
  const [searchTerm, setSearchTerm] = useState("")
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null)
  const [loadingAudio, setLoadingAudio] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.src = ''
        audioRef.current.oncanplaythrough = null
        audioRef.current.onended = null
        audioRef.current.onerror = null
      }
    }
  }, [])

  const filteredVoices = useMemo(
    () =>
      voices.filter((voice) => {
        const search = searchTerm.toLowerCase()
        return (
          voice.name.toLowerCase().includes(search) ||
          voice.description?.toLowerCase().includes(search) ||
          voice.category?.toLowerCase().includes(search) ||
          Object.values(voice.labels || {}).some((label) =>
            label.toLowerCase().includes(search)
          )
        )
      }),
    [voices, searchTerm],
  )

  const handlePlayPreview = async (voice: Voice) => {
    if (!voice.previewUrl) return

    // If currently playing this voice, stop it
    if (playingVoiceId === voice.id) {
      audioRef.current?.pause()
      setPlayingVoiceId(null)
      return
    }

    // Cleanup previous audio element to prevent memory leaks
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.src = ''
      audioRef.current.oncanplaythrough = null
      audioRef.current.onended = null
      audioRef.current.onerror = null
    }

    setLoadingAudio(voice.id)

    try {
      const audio = new Audio(voice.previewUrl)
      audioRef.current = audio

      audio.oncanplaythrough = () => {
        setLoadingAudio(null)
        setPlayingVoiceId(voice.id)
        audio.play()
      }

      audio.onended = () => {
        setPlayingVoiceId(null)
      }

      audio.onerror = () => {
        setLoadingAudio(null)
        setPlayingVoiceId(null)
      }

      audio.load()
    } catch (err) {
      setLoadingAudio(null)
      console.error("Failed to play audio:", err)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading voices...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-600">
        {error}
      </div>
    )
  }

  const selectedVoice = voices.find((v) => v.id === selectedVoiceId)

  return (
    <TooltipProvider delayDuration={300}>
      <div className="min-w-0 w-full space-y-3 overflow-hidden">
        <Label>Voice *</Label>

        {/* Selected voice display */}
        {selectedVoice && (
          <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg overflow-hidden">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Volume2 className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0 overflow-hidden">
              <div className="font-medium text-blue-900 truncate">{selectedVoice.name}</div>
              {selectedVoice.description && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="text-sm text-blue-700 truncate">
                      {selectedVoice.description}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-sm">
                    <p>{selectedVoice.description}</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
          </div>
        )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search voices..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Voice list */}
      <div
        role="region"
        aria-label="Available voices"
        tabIndex={0}
        className="h-[280px] min-w-0 w-full overflow-x-hidden overflow-y-auto overscroll-contain rounded-lg border [scrollbar-gutter:stable]"
      >
        <div className="min-w-0 p-2 space-y-1">
          {filteredVoices.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No voices found
            </div>
          ) : (
            filteredVoices.map((voice) => {
              const isSelected = voice.id === selectedVoiceId
              const isPlaying = playingVoiceId === voice.id
              const isLoadingThis = loadingAudio === voice.id
              return (
                <div
                  key={voice.id}
                  role="button"
                  tabIndex={0}
                  className={cn(
                    "flex min-w-0 w-full items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors overflow-hidden",
                    isSelected
                      ? "bg-blue-50 border border-blue-200"
                      : "hover:bg-muted/50 border border-transparent"
                  )}
                  onClick={() => onSelect(voice.id)}
                  onKeyDown={(e) => {
                    if (e.target !== e.currentTarget) return
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onSelect(voice.id)
                    }
                  }}
                >
                  {/* Avatar */}
                  <div
                    className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0",
                      isSelected
                        ? "bg-blue-100 text-blue-700"
                        : "bg-muted text-muted-foreground"
                    )}
                  >
                    <VoiceGenderIcon labels={voice.labels} />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <div className="flex items-center gap-1.5">
                      <span className={cn(
                        "font-medium text-sm truncate",
                        isSelected ? "text-blue-900" : "text-foreground"
                      )}>
                        {voice.name}
                      </span>
                    </div>
                    {voice.description && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <p className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5">
                            {voice.description}
                          </p>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-sm">
                          <p>{voice.description}</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>

                  {/* Play button */}
                  {voice.previewUrl && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label={`${isPlaying ? "Stop" : "Play"} preview for ${voice.name}`}
                      aria-busy={isLoadingThis}
                      className={cn(
                        "flex-shrink-0 h-9 w-9 p-0 sm:h-7 sm:w-7",
                        isPlaying && "text-blue-600"
                      )}
                      onClick={(e) => {
                        e.stopPropagation()
                        handlePlayPreview(voice)
                      }}
                    >
                      {renderPreviewIcon(isLoadingThis, isPlaying)}
                    </Button>
                  )}

                  {/* Selected indicator */}
                  {isSelected && (
                    <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  )}
                </div>
              )
            })
          )}
        </div>
      </div>

        <p className="text-xs text-muted-foreground">
          {filteredVoices.length} <span>voices available. Click play to preview.</span>
        </p>
      </div>
    </TooltipProvider>
  )
}

function renderPreviewIcon(isLoading: boolean, isPlaying: boolean) {
  if (isLoading) return <Loader2 className="w-3.5 h-3.5 animate-spin" />
  if (isPlaying) return <Pause className="w-3.5 h-3.5" />
  return <Play className="w-3.5 h-3.5" />
}
